// frontend/src/pages/LandingPage/AboutPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Heart, 
    Target, 
    Zap, 
    Users, 
    Shield, 
    Rocket,
    CheckCircle2,
    ArrowRight
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { Link } from 'react-router-dom';

const AboutPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);
    const pillars = [
        {
            id: 1,
            title: 'A Busca Implacável pela Eficiência',
            subtitle: 'Para Quem Cuida',
            description: 'Entendemos o valor do tempo e a pressão dos profissionais de saúde. Nossa tecnologia é construída para:',
            items: [
                {
                    icon: Zap,
                    title: 'Automatizar',
                    text: 'Transformamos tarefas repetitivas (como agendamentos recorrentes e alertas de medicação) em fluxos automáticos.'
                },
                {
                    icon: Target,
                    title: 'Clareza de Dados',
                    text: 'Apresentamos indicadores de saúde (KPIs) e dados financeiros em um único painel, permitindo decisões rápidas e embasadas.'
                },
                {
                    icon: Shield,
                    title: 'Segurança',
                    text: 'Garantimos a conformidade com a LGPD e a segurança dos dados sensíveis, para que você se preocupe apenas com o paciente.'
                }
            ],
            color: 'blue'
        },
        {
            id: 2,
            title: 'O Auxílio a Quem Mais Precisa',
            subtitle: 'Para o Paciente e Família',
            description: 'Acreditamos que a verdadeira eficiência se traduz em um cuidado melhor. Ao organizar e rastrear o plano de cuidados, o Higeia.tech garante que:',
            items: [
                {
                    icon: CheckCircle2,
                    title: 'Nenhum cuidado seja esquecido',
                    text: 'Tarefas e medicações críticas são sempre monitoradas.'
                },
                {
                    icon: Users,
                    title: 'O Histórico é Centralizado',
                    text: 'Independentemente de quem está cuidando, a informação do paciente é completa, rápida e acessível.'
                },
                {
                    icon: Heart,
                    title: 'Transparência',
                    text: 'A família e os envolvidos têm acesso coordenado, promovendo a confiança e a colaboração em torno da saúde do indivíduo.'
                }
            ],
            color: 'green'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main>
                {/* Hero Section da Página */}
                <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative py-20 sm:py-24 bg-gradient-to-br from-higeia-dark via-higeia-medium to-higeia-dark text-white overflow-hidden"
                >
                    {/* Padrão decorativo */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
                        >
                            <Heart className="h-10 w-10" />
                        </motion.div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                            Nossa{' '}
                            <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                História
                            </span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                            O Propósito por Trás do Higeia.tech
                        </p>
                    </div>
                </motion.section>

                {/* Seção: Nossa História */}
                <section className="py-16 sm:py-20 bg-white">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-12"
                        >
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-6">
                                Uma Revolução na{' '}
                                <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                    Gestão de Cuidados
                                </span>
                            </h2>
                        </motion.div>

                        <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1, duration: 0.6 }}
                            >
                                O Higeia.tech nasceu de uma premissa simples, mas profunda: <strong className="text-gray-900">a gestão de cuidados de saúde não pode ser um obstáculo</strong>. Vimos a lacuna entre a tecnologia robusta disponível em grandes hospitais e a realidade de clínicas, cuidadores e organizações menores que lutam diariamente contra a ineficiência de papéis e planilhas desconectadas.
                            </motion.p>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2, duration: 0.6 }}
                                className="text-xl font-semibold text-gray-900"
                            >
                                Somos uma empresa jovem e ágil, impulsionada pela visão de que a tecnologia de ponta deve ser acessível e direcionada ao impacto real.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Seção: Nossa Missão */}
                <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-3xl p-8 sm:p-12 text-white shadow-2xl"
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                    <Target className="h-8 w-8" />
                                </div>
                                <h2 className="text-3xl sm:text-4xl font-black">
                                    Nossa Missão
                                </h2>
                            </div>
                            <p className="text-xl sm:text-2xl text-white/95 leading-relaxed">
                                Simplificar a complexidade da saúde, capacitando profissionais e cuidadores com ferramentas eficientes, seguras e humanas, para que o foco volte para onde ele sempre deveria estar: <strong>no bem-estar do paciente</strong>.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Seção: Nossos Pilares */}
                <section className="py-16 sm:py-20 bg-white">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-16"
                        >
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
                                Nossos{' '}
                                <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                    Pilares
                                </span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                O Higeia.tech não é apenas um software de Prontuário Eletrônico; é uma plataforma que integra a gestão administrativa à assistência. Nossos objetivos claros se apoiam em dois pilares inegociáveis:
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {pillars.map((pillar, index) => {
                                const colorClasses = {
                                    blue: {
                                        bg: 'bg-blue-50',
                                        border: 'border-blue-200',
                                        icon: 'text-blue-600',
                                        title: 'text-blue-900'
                                    },
                                    green: {
                                        bg: 'bg-green-50',
                                        border: 'border-green-200',
                                        icon: 'text-green-600',
                                        title: 'text-green-900'
                                    }
                                };
                                const colors = colorClasses[pillar.color];

                                return (
                                    <motion.div
                                        key={pillar.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.2, duration: 0.6 }}
                                        className={`bg-white rounded-3xl p-8 shadow-xl border-2 ${colors.border} hover:shadow-2xl transition-all duration-300`}
                                    >
                                        <div className="mb-6">
                                            <h3 className={`text-2xl font-black ${colors.title} mb-2`}>
                                                {pillar.id}. {pillar.title}
                                            </h3>
                                            <p className="text-gray-600 font-semibold">
                                                {pillar.subtitle}
                                            </p>
                                        </div>

                                        <p className="text-gray-700 mb-6 leading-relaxed">
                                            {pillar.description}
                                        </p>

                                        <div className="space-y-4">
                                            {pillar.items.map((item, idx) => {
                                                const Icon = item.icon;
                                                return (
                                                    <motion.div
                                                        key={idx}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: 0.3 + index * 0.2 + idx * 0.1 }}
                                                        className="flex items-start gap-4"
                                                    >
                                                        <div className={`flex-shrink-0 w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                                                            <Icon className={`h-6 w-6 ${colors.icon}`} />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-gray-900 mb-1">
                                                                {item.title}
                                                            </h4>
                                                            <p className="text-gray-600 leading-relaxed">
                                                                {item.text}
                                                            </p>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Seção: O Futuro */}
                <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-12"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-higeia-light rounded-full mb-6">
                                <Rocket className="h-8 w-8 text-higeia-dark" />
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-6">
                                O Futuro com{' '}
                                <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                    Higeia.tech
                                </span>
                            </h2>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-gray-100"
                        >
                            <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                Como um Micro-SaaS em crescimento, nosso futuro é impulsionado pela melhoria contínua e pela escuta ativa dos nossos usuários. Continuaremos a expandir nossas ferramentas, como o módulo financeiro e a integração de dispositivos de monitoramento, para garantir que o Higeia.tech seja a solução mais completa e intuitiva no mercado de gestão de cuidados.
                            </p>
                            <p className="text-xl font-bold text-gray-900 text-center">
                                Junte-se a nós nesta jornada para transformar a gestão de saúde, uma organização por vez.
                            </p>
                        </motion.div>

                        {/* CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="mt-12 text-center"
                        >
                            <Link to="/demonstracao">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <PrimaryButton className="px-10 py-4 text-lg font-bold bg-higeia-dark hover:bg-higeia-medium shadow-xl">
                                        Começar sua Jornada
                                        <ArrowRight className="h-5 w-5 ml-2" />
                                    </PrimaryButton>
                                </motion.div>
                            </Link>
                        </motion.div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default AboutPage;

